import React, { useRef, useState } from 'react';

import ProviderList, { FROM_SEARCH_MAP } from '../../../shared/ProviderListV9';

import BottomSheet from '../../../shared/BottomSheet';
import MapCard from '../MapCard';
import LoadMoreButton from '../../../shared/LoadMoreButton';
import LoadingOverlay from '../../../shared/loading';

import {
  MapCardMobileWrapper,
  ProviderListWrapper,
  SortControlContainer
} from './styles';
import { messages } from '../../lib/messages';
import {
  PROVIDER_DRAWER_OPEN,
  LOADING_MORE_PROVIDERS,
  RENDER_PROVIDERS,
  RENDER_SINGLE_LOCATION,
  NO_RESULTS,
  ERROR,
  FETCHING_PROVIDERS
} from '../../../machines/map-search-v9/map-search-states';
import {
  CLOSE_PROVIDER_DRAWER,
  LOAD_MORE_PROVIDERS
} from '../../../machines/map-search/map-search-actions';
import {
  getProvidersFromV9Results,
  getSlots,
  getTotalProvidersFromV9Results
} from '../../../behaviors/search-map-v9';
import { getRelativeParameterizedBookingUrl } from '../../../utils/getRelativeParameterizedBookingUrl';
import {
  CLICK_CLOSE_PROVIDER_DRAWER,
  CLICK_LOAD_MORE_PROVIDERS,
  CLICK_MAPS_SWIPE_TO_VIEW_PROVIDERS
} from '../../lib/constants';
import { SearchSummary } from '../../../shared/SearchSummary';
import { MapsNoResultsBanner, MapsErrorBanner } from '../ErrorBanner';
import SortMenu from '../../../shared/sort-menu';
import useKeyboardAccessibilityFocus from '../../hooks/keyboard-accessibility-focus';
import { gMapLatLngForMloc } from '../../lib/utils';
import { useKeydownListener } from '../../../utils/hooks';

function SmallMediumView({
  intl,
  state,
  send,
  onLocationLinkClick,
  currentQuery,
  config,
  searchSummary,
  sortOptions,
  getUpdatedLocations,
  log,
  location,
  customerCode,
  productName,
  gmapRef
}) {
  const { providersForDrawer, hasMoreProvidersToLoad, selectedLocation } =
    state.context;

  const [swipeableDrawerOpen, setSwipeableDrawerOpen] = useState(false);

  // generate a ref so we can autofocus the close button
  // when the drawer is opened (for keyboard accessibility)
  const closeButtonRef = useRef();

  // for small screens, the loading overlay needs to cover the map for fetching providers,
  // so the match for that is in map-search.jsx. for load more though, the overlay needs to
  // be in the bottom sheet
  const isDrawerInLoadingState = [
    `${PROVIDER_DRAWER_OPEN}.${FETCHING_PROVIDERS}`,
    `${PROVIDER_DRAWER_OPEN}.${LOADING_MORE_PROVIDERS}`
  ].some(state.matches);

  // autofocus the close button when the drawer is opened
  // (so keyboard tabbing enters the drawer)
  const { focusPin } = useKeyboardAccessibilityFocus(
    closeButtonRef,
    state,
    isDrawerInLoadingState
  );

  const onCloseProviderDrawerClick = () => {
    log(CLICK_CLOSE_PROVIDER_DRAWER);
    setSwipeableDrawerOpen(false);

    // recenter map to pin
    if (gmapRef.current) {
      gmapRef.current.panTo(gMapLatLngForMloc(selectedLocation));
    }
    // put focus back onto map pin (for keyboard accessibility)
    focusPin();

    send(CLOSE_PROVIDER_DRAWER);
  };

  const onLoadMoreProvidersClick = () => {
    log(CLICK_LOAD_MORE_PROVIDERS);
    send(LOAD_MORE_PROVIDERS);
  };

  // close drawer on escape key
  useKeydownListener('Escape', () => {
    if (state.matches(`${PROVIDER_DRAWER_OPEN}`) && !isDrawerInLoadingState) {
      onCloseProviderDrawerClick();
    }
  });

  const providers = getProvidersFromV9Results(providersForDrawer) ?? [];
  const { slots, slotsStatus } = getSlots(providers);

  let headerText = '';
  let mapCardContent = null;
  let drawerContent = null;

  // drawer state for bookable location/urgent care clinic
  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${RENDER_SINGLE_LOCATION}`)) {
    return (
      <MapCardMobileWrapper>
        <MapCard
          locationData={selectedLocation}
          currentQuery={currentQuery}
          ctaText={intl.formatMessage(messages.bookAppointmentOnline)}
          bookOnlineUrl={getRelativeParameterizedBookingUrl({
            provider: providers[0],
            config,
            location
          })}
          onMobileCancelHandler={onCloseProviderDrawerClick}
          onLocationClick={onLocationLinkClick}
          closeButtonRef={closeButtonRef}
        />
      </MapCardMobileWrapper>
    );
  }

  // drawer state for lists of providers at a location
  // display current contents during load more (loading overlay is in parent)
  if (
    state.matches(`${PROVIDER_DRAWER_OPEN}.${LOADING_MORE_PROVIDERS}`) ||
    state.matches(`${PROVIDER_DRAWER_OPEN}.${RENDER_PROVIDERS}`)
  ) {
    headerText = (
      <SearchSummary
        totalProviders={getTotalProvidersFromV9Results(providersForDrawer)}
        searchSummary={searchSummary}
        getUpdatedSearch={getUpdatedLocations}
        locationName={selectedLocation.name}
        log={log}
      />
    );
    mapCardContent = (
      <MapCardMobileWrapper>
        <MapCard
          locationData={selectedLocation}
          currentQuery={currentQuery}
          ctaText={intl.formatMessage(messages.viewProvidersAtLocation)}
          onCtaClickHandler={() => {
            setSwipeableDrawerOpen(true);
            log(CLICK_MAPS_SWIPE_TO_VIEW_PROVIDERS, {
              mlocName: selectedLocation.name,
              mlocId: selectedLocation.id
            });
          }}
          onMobileCancelHandler={onCloseProviderDrawerClick}
          onLocationClick={onLocationLinkClick}
          closeButtonRef={closeButtonRef}
        />
      </MapCardMobileWrapper>
    );
    drawerContent = (
      <ProviderListWrapper data-testid="ProviderDrawerContent">
        <SortControlContainer>
          <SortMenu
            searchSummary={searchSummary}
            sortOptions={sortOptions}
            getUpdatedSearch={getUpdatedLocations}
          />
        </SortControlContainer>
        <ProviderList
          providers={providers}
          customerCode={customerCode}
          productName={productName}
          config={config}
          searchSummary={searchSummary}
          log={log}
          shouldAppendAnalyticsParamsBookOnlineUrl={true}
          profileSummaryParams={{
            from: FROM_SEARCH_MAP,
            location_id: selectedLocation.id
          }}
          slots={slots}
          slotsStatus={slotsStatus}
        />
        {hasMoreProvidersToLoad && (
          <LoadMoreButton onLoadMoreProvidersClick={onLoadMoreProvidersClick} />
        )}
      </ProviderListWrapper>
    );
  }

  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${NO_RESULTS}`)) {
    !swipeableDrawerOpen && setSwipeableDrawerOpen(true);
    drawerContent = (
      <MapsNoResultsBanner
        className="smallView"
        messages={messages}
        log={log}
      />
    );
  }

  if (state.matches(`${PROVIDER_DRAWER_OPEN}.${ERROR}`)) {
    !swipeableDrawerOpen && setSwipeableDrawerOpen(true);
    drawerContent = (
      <MapsErrorBanner className="smallView" messages={messages} log={log} />
    );
  }

  return (
    <React.Fragment>
      {mapCardContent}
      <BottomSheet
        data-testid="BottomSheet"
        handleClose={onCloseProviderDrawerClick}
        isOpen={swipeableDrawerOpen}
        headerText={headerText}
      >
        <LoadingOverlay loading={isDrawerInLoadingState} />
        {drawerContent}
      </BottomSheet>
    </React.Fragment>
  );
}

export default SmallMediumView;
